import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { uniqBy, filter, negate } from 'lodash';
import { TripTypes } from '@model/common';
import { TourByTypeProps, TourHit } from '@model/search';
import { TourSearchState, TourSearchParams } from '@model/state/search-forms-state';
import { getToursStateNameByType, isSingleTour } from '@util/tours';

export enum TourSearchActions {
  SLICE_NAME = '@TOUR_SEARCH',
  SET_TOUR_BY_TYPE = '@TOUR_SEARCH/SET_TOUR_BY_TYPE',
  UNSET_TOUR_BY_TYPE = '@TOUR_SEARCH/UNSET_TOUR_BY_TYPE',
  CLEAR_TOURS_BY_TYPE = '@TOUR_SEARCH/CLEAR_TOURS_BY_TYPE',
  SET_SEARCH_PARAMS = '@TOUR_SEARCH/SET_SEARCH_PARAMS',
  SET_TOUR_LANDING_SEARCH = '@TOUR_SEARCH/SET_TOUR_LANDING_SEARCH',
  HYDRATE_SEARCH = '@TOUR_SEARCH/HYDRATE_SEARCH'
}

interface SetTourSearchParams extends TourSearchParams {
  tripType: TripTypes;
  setSearchType?: boolean;
}

export const INITIAL_TOUR_SEARCH_STATE: TourSearchState = {
  escortedTours: [],
  privateTours: [],
  riverCruises: [],
  multiCentres: [],
  cruises: [],
  soloTours: [],
  searchParams: {},
  tourLandingSearch: false,
  latestSearchType: null
};

const uniqName = (tour: TourHit): string => tour.name;

export const addTour = (tour: TourHit, tours: Array<TourHit>): Array<TourHit> => {
  if (isSingleTour(tour)) {
    // not an "All tours in X" tour
    return [tour];
  }
  return uniqBy(filter(tours || [], negate(isSingleTour)).concat([tour]), uniqName);
};

export const removeTour = (tour: TourHit, tours: Array<TourHit>): Array<TourHit> => {
  return (tours || []).filter((selectedTour) => selectedTour.objectID !== tour.objectID);
};

const tourSearchSlice = createSlice({
  name: TourSearchActions.SLICE_NAME,
  initialState: INITIAL_TOUR_SEARCH_STATE,
  reducers: {
    setTourByType: (state, action: PayloadAction<TourByTypeProps>) => {
      const { tripType, tour } = action.payload;
      const stateName = getToursStateNameByType(tripType);
      const existingTours = state[stateName];
      state[stateName] = addTour(tour, existingTours);
    },
    unsetTourByType: (state, action: PayloadAction<TourByTypeProps>) => {
      const { tripType, tour } = action.payload;
      const stateName = getToursStateNameByType(tripType);
      const existingTours = state[stateName];
      state[stateName] = removeTour(tour, existingTours);
    },
    clearToursByType: (state, action: PayloadAction<any>) => {
      const stateName = getToursStateNameByType(action.payload);
      state[stateName] = INITIAL_TOUR_SEARCH_STATE[stateName];
    },
    setSearchParams: (state, action: PayloadAction<SetTourSearchParams>) => {
      const { tripType, setSearchType, ...params } = action.payload;
      if (setSearchType) {
        state.latestSearchType = tripType;
      }
      state.searchParams[tripType] = {
        ...state.searchParams[tripType],
        ...params
      };
    },
    setTourLandingSearch: (state, action: PayloadAction<boolean>) => {
      state.tourLandingSearch = action.payload;
    },
    hydrateSearch: (state, action: PayloadAction<any>) => {
      const {
        searchType,
        duration,
        month,
        from: airports,
        occupancy,
        escortedTours,
        privateTours,
        multiCentres,
        riverCruises,
        cruises,
        soloTours
      } = action.payload;

      if (escortedTours) state.escortedTours = escortedTours;
      if (privateTours) state.privateTours = privateTours;
      if (riverCruises) state.riverCruises = riverCruises;
      if (multiCentres) state.multiCentres = multiCentres;
      if (cruises) state.cruises = cruises;
      if (soloTours) state.soloTours = soloTours;

      // Convert duration from string to number if needed
      const parsedDuration = duration ? (typeof duration === 'string' ? parseInt(duration, 10) : duration) : undefined;

      state.searchParams[searchType] = {
        ...state.searchParams[searchType],
        ...(parsedDuration !== undefined && !isNaN(parsedDuration) && { duration: parsedDuration }),
        ...(month && { month }),
        ...(airports && { airports }),
        ...(occupancy && { occupancy })
      };
    }
  }
});

export const {
  setTourByType,
  unsetTourByType,
  clearToursByType,
  setSearchParams,
  setTourLandingSearch,
  hydrateSearch
} = tourSearchSlice.actions;

export const tourSearchReducer = tourSearchSlice.reducer;
